import { zodResolver } from "@hookform/resolvers/zod";
import {
	AUTH_ERROR_CODES,
	type AuthErrorCode,
	type AuthResponse,
	isApiError,
	OAuthProvider,
	type OAuthProvider as OAuthProviderType,
	type RequestOtpPayload,
	requestOtpSchema,
	AuthMethod as SharedAuthMethod,
} from "@upsquad/shared";
import { Mail } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { AuthLayout } from "@/src/auth/components/auth-layout";
import { AuthMethodError } from "@/src/auth/components/auth-method-error";
import { GoogleAccountModal } from "@/src/auth/components/modals/google-account-modal";
import { MicrosoftAccountModal } from "@/src/auth/components/modals/microsoft-account-modal";
import { OtpVerifyModal } from "@/src/auth/components/modals/otp-verify-modal";
import { WelcomeBackModal } from "@/src/auth/components/modals/welcome-back-modal";
import { useAuthSuccess } from "@/src/auth/hooks/use-auth-success";
import {
	useCreateExchangeCodeMutation,
	useGoogleAuthMutation,
	useMicrosoftAuthMutation,
	useRequestOtpMutation,
} from "@/src/auth/services/auth-http.service";
import { initializeGoogleAuth, promptGoogleOneTap, triggerGoogleSignIn } from "@/src/auth/services/google-oauth.service";
import { initializeMicrosoftAuth, signInWithMicrosoftPopup } from "@/src/auth/services/microsoft-oauth.service";
import { getIsAuthenticated, getLastUsedAuthMethod, getRefreshToken } from "@/src/auth/store/auth-selectors";
import { setLastUsedAuthMethod, setRefreshToken as setPersistedRefreshToken } from "@/src/auth/store/auth-slice";
import { GoogleIcon, MicrosoftIcon } from "@/src/common/assets/icons";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Button, FieldError, InputGroup, Separator, Spinner, TextField } from "@/src/common/components/ui";
import { COMMON_ROUTES } from "@/src/common/constants/common-routes";
import { env } from "@/src/common/constants/env";
import { buildCommunityUrl, buildMainAppUrl, getSubdomain } from "@/src/common/lib/subdomain";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { useGetPublicCommunityQuery } from "@/src/common/services/community-public-http.service";
import { setAccessToken, setRefreshToken } from "@/src/common/services/http.service";
import { setVariant } from "@/src/common/store/general-slice";
import { POST_ROUTES } from "@/src/post/constants/post-routes";
import { LastUsedBadge } from "../components/last-used-badge";

function isHttpError(error: unknown): error is { status: number } {
	return typeof error === "object" && error !== null && "status" in error && typeof (error as { status: unknown }).status === "number";
}

export default function AuthPage() {
	const { t } = useTranslation("auth");
	const dispatch = useAppDispatch();
	const lastUsedAuthMethod = useAppSelector(getLastUsedAuthMethod);
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const storedRefreshToken = useAppSelector(getRefreshToken);
	const [requestOtp, { isLoading }] = useRequestOtpMutation();
	const [googleAuth, { isLoading: isGoogleLoading }] = useGoogleAuthMutation();
	const [microsoftAuth, { isLoading: isMicrosoftLoading }] = useMicrosoftAuthMutation();
	const [createExchangeCode] = useCreateExchangeCodeMutation();
	const handleAuthSuccess = useAuthSuccess();
	const [otpEmail, setOtpEmail] = useState<string | null>(null);
	const [authMethodError, setAuthMethodError] = useState<{ error: AuthErrorCode; provider?: OAuthProviderType } | null>(null);
	const [showGoogleModal, setShowGoogleModal] = useState(false);
	const [showMicrosoftModal, setShowMicrosoftModal] = useState(false);
	const [isGooglePending, setIsGooglePending] = useState(false);
	const [isMicrosoftPending, setIsMicrosoftPending] = useState(false);
	const [welcomeBack, setWelcomeBack] = useState<{ firstName: string; redirectUrl: string } | null>(null);
	const [hasJustValidatedOAuth, setHasJustValidatedOAuth] = useState(false);

	const subdomain = getSubdomain();
	const { data: communityInfo, error: communityError } = useGetPublicCommunityQuery(subdomain ?? "", { skip: !subdomain });

	const params = new URLSearchParams(window.location.search);
	const popupModeParam = params.get("mode");
	const isGooglePopupMode = popupModeParam === OAuthProvider.GOOGLE;
	const isMicrosoftPopupMode = popupModeParam === OAuthProvider.MICROSOFT;
	const isPopupMode = isGooglePopupMode || isMicrosoftPopupMode;
	const popupRedirectSlug = params.get("redirect");

	useEffect(() => {
		if (communityInfo?.variant) {
			document.documentElement.setAttribute("data-variant", communityInfo.variant);
			dispatch(setVariant(communityInfo.variant));
		}
		return () => {
			if (communityInfo?.variant) {
				document.documentElement.removeAttribute("data-variant");
			}
		};
	}, [communityInfo?.variant, dispatch]);

	const isFormDisabled = isGooglePending || isGoogleLoading || isMicrosoftPending || isMicrosoftLoading;

	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<RequestOtpPayload>({
		resolver: zodResolver(requestOtpSchema),
	});

	const isAuthResponse = (value: unknown): value is AuthResponse => {
		return (
			!!value &&
			typeof value === "object" &&
			"user" in value &&
			"access_token" in value &&
			"refresh_token" in value &&
			"communities" in value
		);
	};

	const processAuthResult = useCallback(
		async (result: unknown) => {
			if (!isAuthResponse(result)) return false;

			const { access_token, refresh_token, user, communities } = result;

			if (isPopupMode && popupRedirectSlug) {
				const { code } = await createExchangeCode({ refresh_token }).unwrap();
				window.location.href = `${buildCommunityUrl(popupRedirectSlug, POST_ROUTES.FEED)}?auth_code=${encodeURIComponent(code)}`;
				return true;
			}

			const singleCommunity = communities[0];
			if (communities.length === 1 && singleCommunity) {
				setAccessToken(access_token);
				setRefreshToken(refresh_token);
				dispatch(setPersistedRefreshToken(refresh_token));
				const baseUrl = buildCommunityUrl(singleCommunity.slug, POST_ROUTES.FEED);
				const { code } = await createExchangeCode({ refresh_token }).unwrap();
				const redirectUrl = `${baseUrl}?auth_code=${encodeURIComponent(code)}`;
				setWelcomeBack({
					firstName: user.first_name ?? "",
					redirectUrl,
				});
			} else {
				handleAuthSuccess(result);
			}
			return true;
		},
		[handleAuthSuccess, isPopupMode, popupRedirectSlug, createExchangeCode, dispatch],
	);

	const handleGoogleCredential = useCallback(
		async (idToken: string) => {
			setAuthMethodError(null);
			setIsGooglePending(true);
			setHasJustValidatedOAuth(true);
			try {
				const result = await googleAuth({ id_token: idToken }).unwrap();
				if (await processAuthResult(result)) {
					dispatch(setLastUsedAuthMethod(OAuthProvider.GOOGLE));
				}
			} catch (err: unknown) {
				if (isApiError(err) && err.data.error === AUTH_ERROR_CODES.EMAIL_ACCOUNT) {
					setAuthMethodError({ error: AUTH_ERROR_CODES.EMAIL_ACCOUNT });
				}
			} finally {
				setIsGooglePending(false);
			}
		},
		[googleAuth, processAuthResult, dispatch],
	);

	const handleGoogleClick = () => {
		if (subdomain) {
			window.location.href = buildMainAppUrl(`/auth?mode=google&redirect=${subdomain}`);
			return;
		}

		setIsGooglePending(true);
		triggerGoogleSignIn();
		const checkClosed = setInterval(() => {
			if (document.hasFocus()) {
				clearInterval(checkClosed);
				setIsGooglePending(false);
			}
		}, 500);
	};

	const handleMicrosoftClick = useCallback(async () => {
		if (subdomain) {
			window.location.href = buildMainAppUrl(`/auth?mode=microsoft&redirect=${subdomain}`);
			return;
		}

		setAuthMethodError(null);
		setIsMicrosoftPending(true);
		try {
			const idToken = await signInWithMicrosoftPopup();
			setHasJustValidatedOAuth(true);
			const result = await microsoftAuth({ id_token: idToken }).unwrap();
			if (await processAuthResult(result)) {
				dispatch(setLastUsedAuthMethod(OAuthProvider.MICROSOFT));
			}
		} catch (err: unknown) {
			if (isApiError(err) && err.data.error === AUTH_ERROR_CODES.EMAIL_ACCOUNT) {
				setAuthMethodError({ error: AUTH_ERROR_CODES.EMAIL_ACCOUNT });
			}
		} finally {
			setIsMicrosoftPending(false);
		}
	}, [subdomain, microsoftAuth, processAuthResult, dispatch]);

	const googleInitialized = useRef(false);
	const microsoftInitialized = useRef(false);
	const microsoftPopupTriggered = useRef(false);
	const popupRedirectStarted = useRef(false);
	const isPopupRedirect = isPopupMode && !!popupRedirectSlug;

	useEffect(() => {
		if (!isPopupRedirect || !isAuthenticated || !storedRefreshToken) return;
		if (!hasJustValidatedOAuth) return;
		if (popupRedirectStarted.current) return;
		popupRedirectStarted.current = true;

		const targetSlug = popupRedirectSlug;
		if (!targetSlug) return;

		(async () => {
			try {
				const { code } = await createExchangeCode({ refresh_token: storedRefreshToken }).unwrap();
				window.location.href = `${buildCommunityUrl(targetSlug, POST_ROUTES.FEED)}?auth_code=${encodeURIComponent(code)}`;
			} catch {
				window.location.href = buildCommunityUrl(targetSlug, POST_ROUTES.FEED);
			}
		})();
	}, [isPopupRedirect, isAuthenticated, storedRefreshToken, popupRedirectSlug, createExchangeCode, hasJustValidatedOAuth]);

	useEffect(() => {
		if (!env.GOOGLE_CLIENT_ID) return;
		if (subdomain && !isGooglePopupMode) return;
		if (isPopupRedirect && isAuthenticated) return;
		if (isMicrosoftPopupMode) return;
		if (googleInitialized.current) return;
		googleInitialized.current = true;

		initializeGoogleAuth(env.GOOGLE_CLIENT_ID, handleGoogleCredential).then(() => {
			if (isGooglePopupMode) {
				triggerGoogleSignIn();
			} else {
				promptGoogleOneTap();
			}
		});
	}, [handleGoogleCredential, subdomain, isGooglePopupMode, isMicrosoftPopupMode, isPopupRedirect, isAuthenticated]);

	useEffect(() => {
		if (!env.MICROSOFT_CLIENT_ID) return;
		if (subdomain && !isMicrosoftPopupMode) return;
		if (isPopupRedirect && isAuthenticated) return;
		if (microsoftInitialized.current) return;
		microsoftInitialized.current = true;

		initializeMicrosoftAuth(env.MICROSOFT_CLIENT_ID).then(() => {
			if (isMicrosoftPopupMode && !microsoftPopupTriggered.current) {
				microsoftPopupTriggered.current = true;
				handleMicrosoftClick();
			}
		});
	}, [subdomain, isMicrosoftPopupMode, isPopupRedirect, isAuthenticated, handleMicrosoftClick]);

	const onSubmit = async (data: RequestOtpPayload) => {
		setAuthMethodError(null);
		try {
			const result = await requestOtp({ ...data, community_slug: subdomain }).unwrap();
			if (result && "message" in result && result.message === "otp_sent") {
				dispatch(setLastUsedAuthMethod(SharedAuthMethod.EMAIL));
				setOtpEmail(data.email);
			}
		} catch (err: unknown) {
			if (isApiError(err) && err.data.error === AUTH_ERROR_CODES.OAUTH_ACCOUNT) {
				if (err.data.provider === OAuthProvider.MICROSOFT) {
					setShowMicrosoftModal(true);
				} else {
					setShowGoogleModal(true);
				}
			}
		}
	};

	if (isPopupMode) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="flex flex-col items-center gap-4">
					<Spinner size="lg" />
					<p className="text-foreground/50 text-sm">{isMicrosoftPopupMode ? t("sign_in_microsoft") : t("sign_in_google")}...</p>
				</div>
			</div>
		);
	}

	if (subdomain && communityError && isHttpError(communityError) && communityError.status === 404) {
		return <Navigate to={COMMON_ROUTES.COMMUNITY_NOT_FOUND} replace />;
	}

	const title = communityInfo ? t("welcome_community_title", { communityName: communityInfo.name }) : t("welcome_title");
	const subtitle = communityInfo ? t("welcome_community_subtitle") : t("welcome_subtitle");

	return (
		<>
			<PageHelmet title={title} />
			<AuthLayout>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-2">
						<h1 className="text-3xl font-semibold">{title}</h1>
						<p className="text-smtext-foreground">{subtitle}</p>
					</div>

					{authMethodError && (
						<AuthMethodError error={authMethodError.error} provider={authMethodError.provider} onDismiss={() => setAuthMethodError(null)} />
					)}

					<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
						<Controller
							name="email"
							control={control}
							defaultValue=""
							render={({ field }) => (
								<TextField isInvalid={!!errors.email} isRequired>
									<InputGroup>
										<InputGroup.Prefix>
											<Mail size={20} />
										</InputGroup.Prefix>
										<InputGroup.Input
											type="email"
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											placeholder={t("email_placeholder")}
										/>
									</InputGroup>
									{errors.email?.message && <FieldError>{errors.email.message}</FieldError>}
								</TextField>
							)}
						/>
						<div className="relative">
							<Button type="submit" variant="primary" className="w-full" isPending={isLoading} isDisabled={!isValid || isFormDisabled}>
								{({ isPending }) => (
									<>
										{isPending ? <Spinner color="current" size="sm" /> : null}
										{t("get_started")}
									</>
								)}
							</Button>
							{lastUsedAuthMethod === SharedAuthMethod.EMAIL && <LastUsedBadge />}
						</div>
					</form>

					<div className="flex items-center gap-3">
						<Separator className="flex-1" />
						<span className="text-foreground/40 text-sm">{t("or")}</span>
						<Separator className="flex-1" />
					</div>

					<div className="flex flex-col gap-3">
						{env.GOOGLE_CLIENT_ID && (
							<div className="relative">
								<Button variant="outline" className="w-full" onPress={handleGoogleClick} isPending={isGooglePending || isGoogleLoading}>
									{({ isPending }) => (
										<>
											{isPending ? <Spinner color="current" size="sm" /> : <GoogleIcon />}
											{t("sign_in_google")}
										</>
									)}
								</Button>
								{lastUsedAuthMethod === OAuthProvider.GOOGLE && <LastUsedBadge />}
							</div>
						)}
						{env.MICROSOFT_CLIENT_ID && (
							<div className="relative">
								<Button
									variant="outline"
									className="w-full"
									isDisabled={!env.MICROSOFT_CLIENT_ID}
									onPress={handleMicrosoftClick}
									isPending={isMicrosoftPending || isMicrosoftLoading}
								>
									{({ isPending }) => (
										<>
											{isPending ? <Spinner color="current" size="sm" /> : <MicrosoftIcon />}
											{t("sign_in_microsoft")}
										</>
									)}
								</Button>
								{lastUsedAuthMethod === OAuthProvider.MICROSOFT && <LastUsedBadge />}
							</div>
						)}
					</div>
				</div>

				{otpEmail && (
					<OtpVerifyModal
						email={otpEmail}
						isOpen={!!otpEmail}
						onClose={() => setOtpEmail(null)}
						onAuthSuccess={async (result) => {
							dispatch(setLastUsedAuthMethod(SharedAuthMethod.EMAIL));
							await processAuthResult(result);
						}}
					/>
				)}
				<GoogleAccountModal isOpen={showGoogleModal} onClose={() => setShowGoogleModal(false)} onGoogleSignIn={handleGoogleClick} />
				<MicrosoftAccountModal
					isOpen={showMicrosoftModal}
					onClose={() => setShowMicrosoftModal(false)}
					onMicrosoftSignIn={handleMicrosoftClick}
				/>
			</AuthLayout>

			{welcomeBack && <WelcomeBackModal isOpen firstName={welcomeBack.firstName} redirectUrl={welcomeBack.redirectUrl} />}
		</>
	);
}
